import React, { useEffect, useState, useRef } from "react";
import { useCreateLeadMutation } from "../../../app/api/leadApi";
import { useNavigate } from "react-router-dom";

// constants required by backend
const SOURCE = "dbc_webinar_landing";
const EVENT_TYPE = "webinar_signup";

const formatEventDateTime = (date: Date) => {
  // Try to reliably format in Africa/Lagos (WAT) timezone; fallback to simple ISO parts.
  try {
    // Date as YYYY-MM-DD
    const eventDate = new Intl.DateTimeFormat("en-CA", {
      timeZone: "Africa/Lagos",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(date); // en-CA gives YYYY-MM-DD

    // Time as HH:MM (24-hour)
    const eventTime = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Africa/Lagos",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    })
      .format(date)
      .slice(0, 5); // ensure "HH:MM"

    return { eventDate, eventTime };
  } catch (e) {
    // fallback: use UTC-derived parts (less ideal, but safe)
    const iso = date.toISOString(); // e.g. 2025-11-30T09:00:00.000Z depending on timezone
    const [d, t] = iso.split("T");
    const eventDate = d; // yyyy-mm-dd
    const eventTime = t.slice(0, 5); // HH:MM (UTC)
    return { eventDate, eventTime };
  }
};

const WebinarRegistration: React.FC = () => {
  // form states (single source of truth)
  const navigate = useNavigate();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [whatsapp, setWhatsapp] = useState("");

  // UI states
  const [submitted, setSubmitted] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // RTK Query mutation
  const [createLead, { isLoading: mutationLoading }] = useCreateLeadMutation();

  // countdown state
  const [countdown, setCountdown] = useState({
    days: "--",
    hours: "--",
    mins: "--",
    secs: "--",
  });

  // IMPORTANT: compute a target date that is the current day and exactly 5 minutes from
  // the moment the user opens the page. Keep it stable for the session using useRef.
  const targetDateRef = useRef<Date>(new Date(Date.now() + 5 * 60 * 1000));

  // derived event date/time from the dynamic target date
  const { eventDate: derivedEventDate, eventTime: derivedEventTime } =
    formatEventDateTime(targetDateRef.current);

  // ref to the register section so "Reserve My Seat" can scroll to it
  const registerRef = useRef<HTMLFormElement | null>(null);

  // countdown effect (runs once)
  useEffect(() => {
    const tick = () => {
      const now = Date.now();
      const distance = targetDateRef.current.getTime() - now;
      if (distance <= 0) {
        setCountdown({ days: "00", hours: "00", mins: "00", secs: "00" });
        return;
      }
      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      );
      const mins = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const secs = Math.floor((distance % (1000 * 60)) / 1000);

      setCountdown({
        days: String(days).padStart(2, "0"),
        hours: String(hours).padStart(2, "0"),
        mins: String(mins).padStart(2, "0"),
        secs: String(secs).padStart(2, "0"),
      });
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  // Smooth scroll handler for Reserve My Seat
  const handleScrollToRegister = (e?: React.MouseEvent) => {
    e?.preventDefault();
    if (registerRef.current) {
      registerRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      const firstInput = registerRef.current.querySelector(
        "input"
      ) as HTMLInputElement | null;
      firstInput?.focus();
    }
  };

  // Basic phone sanitizer (keeps digits + leading +)
  const normalizePhone = (raw: string) => {
    if (!raw) return "";
    return raw.replace(/[^+\d]/g, "");
  };

  // Basic validation
  const validateForm = () => {
    setLocalError(null);
    if (!fullName.trim() || !email.trim() || !whatsapp.trim()) {
      setLocalError("Please fill in your name, email and WhatsApp number.");
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setLocalError("Please enter a valid email address.");
      return false;
    }
    return true;
  };

  // Single submit handler using RTK mutation
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    setSuccessMessage(null);

    if (!validateForm()) return;

    // Build payload for your backend / leads API
    const payload: any = {
      firstName: fullName.trim(),
      email: email.trim().toLowerCase(),
      phone: normalizePhone(whatsapp.trim()),
      source: SOURCE,
      timestamp: new Date().toISOString(),
      // always include derived date/time and eventType
      eventDate: derivedEventDate,
      eventTime: derivedEventTime,
      eventType: EVENT_TYPE,
    };

    try {
      // call RTK mutation; unwrap() will throw if request fails
      const result = await createLead(payload).unwrap();

      // convention: backend returns { success: true, ... }
      if (result && result.success === true) {
        setSuccessMessage(
          "Success! Your spot is reserved. Check your email and WhatsApp for confirmation."
        );
        setSubmitted(true);
        // optionally clear fields or keep them

        setFullName("");
        setEmail("");
        setWhatsapp("");
        navigate("/dbc/dib-webinar");
      } else {
        // friendly error message when API responds but indicates failure
        setLocalError(
          result?.message || "Could not register. Please try again."
        );
      }
    } catch (err: any) {
      // network / server error
      console.error("createLead error:", err);
      const friendly =
        err?.data?.message ||
        err?.message ||
        "An error occurred while registering. Please try again later.";
      setLocalError(friendly);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F9FC] text-[#1A1A1A]">
      {/* HERO */}
      <header className="bg-[#1B2951] text-white py-16">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-8 ">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold leading-tight">
              FREE Training —
              <br />
              How to Turn Your Skills Into a{" "}
              <span className="text-[#F5A623]">₦500k–₦1m Monthly</span> Online
              Income without Quitting your Job
            </h1>

            <p className=" text-2xl mt-6 text- opacity-90">
              A simple 3-step framework for busy Nigerian professionals —
              discover your profitable sweet spot, grow an audience, and build
              automated sales systems — without quitting your job.
            </p>

            <div className="mt-8 flex gap-4">
              <button
                onClick={handleScrollToRegister}
                className="inline-flex items-center gap-3 bg-[#F5A623] text-[#1B2951] font-semibold py-3 px-5 rounded-lg shadow hover:opacity-95 transition text-2xl"
                aria-label="Reserve My Seat"
              >
                Reserve My Seat — It's Free
              </button>
            </div>

            <div className="mt-6 text-xl opacity-80">
              <strong>Duration:</strong> 30–45 minutes •{" "}
              <strong>Replay:</strong> sent to registered attendees only
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow">
            <div className="text-center">
              <div className="text-2xl text-gray-500">Next Live Session</div>
              <div className="mt-2 text-3xl font-bold text-[#1B2951]">
                {countdown.days}DAYS {countdown.hours}HRS
              </div>
              <div className="text-xl text-gray-500">
                {countdown.mins}M {countdown.secs}S
              </div>
              <div className="text-xl text-gray-500 mt-2">
                {/* show dynamic date/time computed as "now + 5 minutes" */}
                Date:{" "}
                <strong>
                  {new Date(targetDateRef.current).toLocaleDateString("en-GB", {
                    timeZone: "Africa/Lagos",
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}
                  {" • "}
                  {new Date(targetDateRef.current).toLocaleTimeString("en-GB", {
                    timeZone: "Africa/Lagos",
                    hour: "numeric",
                    minute: "2-digit",
                    hour12: false,
                  })}
                </strong>{" "}
                (WAT)
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {/* show derived date/time explicitly */}
                Session date: <strong>{derivedEventDate}</strong> • Time:{" "}
                <strong>{derivedEventTime}</strong> (WAT)
              </div>
            </div>

            <div className="mt-6 border-t pt-6">
              <h4 className="text-xl font-semibold text-[#1B2951]">
                Secure Your Spot
              </h4>
              <p className="text-xl text-gray-600 mt-2">
                Register below and we’ll send details to your email & WhatsApp.
              </p>

              <form
                onSubmit={handleSubmit}
                className="mt-4 grid gap-3"
                ref={(el) => {
                  if (el && !registerRef.current)
                    registerRef.current = el as HTMLFormElement;
                }}
              >
                {/* force dark text color so user input is visible */}
                <input
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  type="text"
                  placeholder="Full name"
                  className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#1B2951] text-[#1A1A1A] placeholder-gray-400"
                  required
                  aria-label="Full name"
                />

                <input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  type="email"
                  placeholder="Email address"
                  className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#1B2951] text-[#1A1A1A] placeholder-gray-400"
                  required
                  aria-label="Email address"
                />

                <input
                  value={whatsapp}
                  onChange={(e) => setWhatsapp(e.target.value)}
                  type="tel"
                  placeholder="WhatsApp number (with country code)"
                  className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#1B2951] text-[#1A1A1A] placeholder-gray-400"
                  required
                  aria-label="WhatsApp number"
                />

                {/* Hidden/read-only metadata - source, eventDate, eventTime, eventType are derived and sent automatically */}
                <input type="hidden" name="source" value={SOURCE} />
                <input
                  type="hidden"
                  name="eventDate"
                  value={derivedEventDate}
                />
                <input
                  type="hidden"
                  name="eventTime"
                  value={derivedEventTime}
                />
                <input type="hidden" name="eventType" value={EVENT_TYPE} />

                <button
                  disabled={mutationLoading || submitted}
                  type="submit"
                  className={`text-xl w-full py-3 rounded-md font-semibold text-white ${
                    mutationLoading || submitted
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-[#1B2951] hover:bg-[#15203F]"
                  }`}
                  aria-live="polite"
                >
                  {mutationLoading
                    ? "Saving..."
                    : submitted
                    ? "You're registered"
                    : "Save my seat"}
                </button>

                {localError && (
                  <p className="text-xl text-red-600 mt-2">{localError}</p>
                )}
                {successMessage && (
                  <p className="text-xl text-green-700 mt-2">
                    {successMessage}
                  </p>
                )}

                <p className="text-xs mt-2 text-gray-500">
                  By registering you agree to receive emails & WhatsApp messages
                  about this event.
                </p>
              </form>
            </div>
          </div>
        </div>
      </header>

      {/* WHAT YOU'LL LEARN */}
      <section id="what" className="py-16 px-6">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-10 items-start">
          <div>
            <h2 className="text-3xl font-bold mb-4">
              What You’ll Learn In This Free Training
            </h2>
            <p className="text-gray-700 mb-6 text-xl">
              You'll walk away in this training knowing with 100% clarity your
              ₦1m a month online business blueprint even with minimal time
              commitment and without having to quit your current job.
            </p>

            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <div className="text-[#F5A623] text-3xl">✔</div>
                <div className="text-xl">
                  <strong>
                    Discover your Personal Revenue Opportunities (PRO):
                  </strong>{" "}
                  Find the intersection of your passion, skill, and market
                  demand.
                </div>
              </li>

              <li className="flex items-start gap-3">
                <div className="text-[#F5A623] text-3xl">✔</div>
                <div className="text-xl">
                  <strong>Validation fast:</strong> How to check your idea in
                  24–72 hours.
                </div>
              </li>

              <li className="flex items-start gap-3">
                <div className="text-[#F5A623] text-3xl">✔</div>
                <div className="text-xl">
                  <strong>Quick visibility wins:</strong> The Visibility Engine™
                  you can use with 2–3 hours/week.
                </div>
              </li>

              <li className="flex items-start gap-3">
                <div className="text-[#F5A623] text-3xl">✔</div>
                <div className="text-xl">
                  <strong>How to get your first paying customer:</strong> The
                  SSRM framework that turns first wins into testimonials &
                  momentum.
                </div>
              </li>
            </ul>
          </div>

          <div className="bg-white rounded-xl p-6 shadow">
            <h3 className="text-3xl font-semibold text-[#1B2951]">
              Who this is for
            </h3>
            <p className="text-gray-700 mt-3 text-xl">
              Busy Nigerian professionals (bankers, teachers, engineers, civil
              servants, managers) who want extra income using skills they
              already have — without quitting their job.
            </p>

            <div className="mt-6">
              <h4 className="font-semibold text-3xl">Why this works</h4>
              <ul className="mt-3 text-xl text-gray-700 space-y-2">
                <li>
                  Built for Nigerian realities — salary cycles, trust cues, and
                  WhatsApp-first behaviour.
                </li>
                <li>
                  Action-first — you leave with a validated idea, not more
                  confusion.
                </li>
                <li>
                  Fast results — see first micro wins in days, not months.
                </li>
              </ul>
            </div>

            <button
              onClick={handleScrollToRegister}
              className="text-2xl mt-6 inline-block bg-[#1B2951] text-white font-semibold py-3 px-5 rounded-md hover:bg-[#15203F]"
            >
              Reserve My Seat
            </button>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      {/* <section className="py-16 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <h3 className="text-3xl font-bold text-center mb-8">
            Real results from Nigerians
          </h3>

          <div className="grid md:grid-cols-3 gap-6">
            <blockquote className="p-6 rounded-lg border">
              <p className="text-gray-800">
                “I got my first ₦45,000 in 10 days after this framework. The
                SSRM challenge helped me get traction fast.” —{" "}
                <strong>Aisha, Lagos</strong>
              </p>
            </blockquote>

            <blockquote className="p-6 rounded-lg border">
              <p className="text-gray-800">
                “This approach is practical and built for busy people. I now
                have a product and a WhatsApp list.” —{" "}
                <strong>Emeka, Abuja</strong>
              </p>
            </blockquote>

            <blockquote className="p-6 rounded-lg border">
              <p className="text-gray-800">
                “I validated my idea in 72 hours and closed my first client.
                Worth every minute.” — <strong>Fatima, Port Harcourt</strong>
              </p>
            </blockquote>
          </div>
        </div>
      </section> */}

      {/* Footer */}
      <footer className="py-10">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <p className="text-xl text-gray-600">
            © {new Date().getFullYear()} Digital Income Blueprint. All rights
            reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default WebinarRegistration;
